import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, BarChart3, FileText, MapPin } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getIndustryBySlug, INDUSTRIES } from '../../data/industries';
import { getCitiesByState, STATES } from '../../data/locations';

export default function IndustryPage() {
  const { slug } = useParams<{ slug: string }>();
  const industry = slug ? getIndustryBySlug(slug) : undefined;

  if (!industry) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Industry Not Found</h1>
            <p className="text-gray-600 mb-8">
              We could not find this industry. Browse all industries below.
            </p>
            <Link to="/industries" className="btn-primary">
              View All Industries
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const related = industry.relatedSlugs
    .map((s) => INDUSTRIES.find((ind) => ind.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <Link
              to="/industries"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              All Industries
            </Link>

            <div className="text-5xl mb-6">{industry.icon}</div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              {industry.headline}
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {industry.description}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
              >
                Try iHireFast Free
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
              >
                Talk to Sales
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
              {industry.stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.number}</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Industry Challenges
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Hiring challenges in {industry.name}
            </h2>
            <p className="text-lg text-gray-600">
              These are the most common hiring pain points in {industry.name.toLowerCase()} — and how
              iHireFast solves each one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {industry.painPoints.map((point, i) => (
              <div key={i} className="card p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How iHireFast Solves It */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              The Solution
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              How iHireFast works for {industry.name}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                step: '01',
                title: 'Build a custom form',
                desc: `Create an application form tailored to ${industry.name.toLowerCase()} with fields that capture exactly what you need.`,
                icon: FileText,
              },
              {
                step: '02',
                title: 'Automate communication',
                desc: 'Every applicant gets an instant auto-reply and can self-schedule interviews through your Google Calendar.',
                icon: CheckCircle,
              },
              {
                step: '03',
                title: 'Track and hire',
                desc: 'Review applicants in your dashboard, move them through your pipeline, and make hiring decisions faster.',
                icon: BarChart3,
              },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-7 h-7 text-primary-600" />
                </div>
                <div className="text-xs font-bold text-primary-600 mb-2">STEP {step.step}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Form Fields */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Sample Form
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Example application form for {industry.name}
            </h2>
            <p className="text-lg text-gray-600">
              Here is a sample of the fields you might include on an application form for{' '}
              {industry.name.toLowerCase()} positions. Customize it however you like.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="card p-8">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-100">
                <div className="text-2xl">{industry.icon}</div>
                <div>
                  <h3 className="font-semibold text-gray-900">{industry.name} Application</h3>
                  <p className="text-sm text-gray-500">Powered by iHireFast</p>
                </div>
              </div>
              <div className="space-y-4">
                {industry.sampleFields.map((field, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded flex items-center justify-center">
                      <CheckCircle className="w-3.5 h-3.5 text-primary-600" />
                    </div>
                    <span className="text-sm text-gray-700">{field}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <a
                  href="https://app.ihirefast.com"
                  className="btn-primary text-sm px-6 py-3 inline-flex items-center gap-2 group"
                >
                  Build This Form
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to hire {industry.name.toLowerCase()} staff faster?
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Join businesses in {industry.name.toLowerCase()} using iHireFast to streamline recruiting.
              Free to start, no credit card required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Top Locations for This Industry */}
      <section className="section-padding bg-white border-t border-gray-200">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Locations
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Hire {industry.name.toLowerCase()} staff by city
            </h2>
            <p className="text-lg text-gray-600">
              Find {industry.name.toLowerCase()} candidates in top cities across our service areas.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {STATES.map((st) => {
              const topCities = getCitiesByState(st.abbr).slice(0, 10);
              if (topCities.length === 0) return null;
              return (
                <div key={st.slug} className="mb-8">
                  <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
                    {st.name}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {topCities.map((loc) => (
                      <Link
                        key={loc.slug}
                        to={`/industries/${industry.slug}/${st.slug}/${loc.slug}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-all"
                      >
                        <MapPin className="w-3.5 h-3.5" />
                        {loc.city}, {loc.stateAbbr}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Industries */}
      {related.length > 0 && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="container-custom">
            <div className="text-center mb-8">
              <h3 className="text-lg font-semibold text-gray-900">Related industries</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {related.map((rel) =>
                rel ? (
                  <Link
                    key={rel.slug}
                    to={`/industries/${rel.slug}`}
                    className="card p-4 group hover:shadow-md transition-all duration-300 text-center"
                  >
                    <div className="text-2xl mb-2">{rel.icon}</div>
                    <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {rel.name}
                    </h4>
                  </Link>
                ) : null,
              )}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
